import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";
import { ClassValue } from "clsx";

type TableCellValue = string | number | React.ReactNode;

export type ColumnConfig<T> = {
  key: keyof T;
  header?: string;
  headerClassName?: ClassValue;
  cellClassName?: ClassValue;
};

type CustomTableProps<T extends Record<string, TableCellValue>> = {
  objectList: T[];
  title?: string;
  className?: string;
  showHeader?: boolean;
  columns?: ColumnConfig<T>[];
  rowClassName?: ClassValue;
};

const CustomTable = <T extends Record<string, TableCellValue>>({
  objectList,
  title,
  className,
  showHeader = true,
  columns,
  rowClassName,
}: CustomTableProps<T>) => {
  if (!objectList || objectList.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        Nenhum dado disponível
      </div>
    );
  }

  const resolvedColumns: ColumnConfig<T>[] =
    columns ??
    (Object.keys(objectList[0]) as (keyof T)[]).map((key) => ({
      key,
      header: String(key),
    }));

  return (
    <div className="w-full overflow-hidden">
      {title && <h2 className="mb-2 text-lg font-semibold">{title}</h2>}

      <div className={cn("rounded-2xl overflow-hidden", className)}>
        <Table className="table-fixed w-full">
          {showHeader && (
            <TableHeader>
              <TableRow>
                {resolvedColumns.map((col) => (
                  <TableHead
                    key={String(col.key)}
                    className={cn("truncate", col.headerClassName)}
                  >
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          )}

          <TableBody>
            {objectList.map((row, rowIndex) => (
              <TableRow key={rowIndex} className={cn("border-b hover:bg-brand-sidebar/5 transition-colors duration-100", rowClassName)}>
                {resolvedColumns.map((col) => (
                  <TableCell
                    key={String(col.key)}
                    className={cn(
                      "truncate overflow-hidden whitespace-nowrap",
                      col.cellClassName
                    )}
                  >
                    {row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CustomTable;
